import { List, ListAlt, Login as LoginIcon, Logout, Search } from '@mui/icons-material'
import { AppBar, Box, CircularProgress, Tab, Tabs, Toolbar, Typography, styled } from '@mui/material'
import axios from 'axios'
import { AuthenticationBearerJWT, isAdmin } from 'chezy-inventory-backend/bin/auth/AuthenticationContext'
import { Inventory } from 'chezy-inventory-backend/bin/database/types/Inventory'
import { ReactNode, useCallback, useContext, useEffect, useState } from 'react'
import { Link, Navigate, Route, Routes, useLocation, useMatch } from 'react-router-dom'

import './App.css'
import { BinBrowser } from './pages/browser'
import { InventoryBinView } from './pages/inventories/inventoryBinView'
import { InventoryBrowser } from './pages/inventories/inventoryBrowser'
import { InventoryManager } from './pages/inventories/inventoryManager'
import { InventoryOverview } from './pages/inventories/inventoryOverview'
import { Login } from './pages/login/login'
import { ItemSearch } from './pages/search'
import { AuthContext } from './utils/authContext'
import { getBaseURL } from './utils/getBaseURL'
import { usePrevious } from './utils/usePrevious'

const StyledTab = styled((props: { label: string; value: string; to: string; icon?: JSX.Element }) => (
  <Tab iconPosition="start" {...props} component={Link} />
))(({ theme }) => ({
  fontWeight: theme.typography.fontWeightRegular,
  marginRight: theme.spacing(1),
  color: 'rgba(255, 255, 255, 0.7)',
  '&.Mui-selected': {
    color: '#fff',
    fontWeight: theme.typography.fontWeightBold,
  },
  '&.Mui-focusVisible': {
    backgroundColor: 'rgba(100, 95, 228, 0.32)',
  },
}))

function LogoutComponent() {
  const authContext = useContext(AuthContext)
  localStorage.clear()
  authContext.setJwt(undefined)
  authContext.setTokenData(undefined)
  return <Navigate to="/login" />
}

function NavTabs() {
  const matchPartialBin = useMatch('/bin/:id')
  const matchBin = useMatch('/bin')
  const matchSearch = useMatch('/search')
  const matchLogin = useMatch('/login')
  const matchLogout = useMatch('/logout')
  const matchPartialInventory = useMatch('/inventory/:id')
  const matchInventory = useMatch('/inventory')

  const authContext = useContext(AuthContext)

  let value = '/'
  if (matchPartialBin || matchBin) {
    value = '/bin'
  }
  if (matchSearch) {
    value = '/search'
  }
  if (matchLogin) {
    value = '/login'
  }
  if (matchLogout) {
    value = '/logout'
  }
  if (matchInventory) {
    value = '/inventory'
  }
  if (matchPartialInventory) {
    value = '/inventory/:id'
  }
  return (
    <Tabs value={value} TabIndicatorProps={{ style: { backgroundColor: 'white' } }}>
      {authContext.tokenData && <StyledTab label="Bin Browser" value="/bin" to="/bin" icon={<List />} />}
      {authContext.tokenData && <StyledTab label="Item Search" value="/search" to="/search" icon={<Search />} />}

      {authContext.tokenData && isAdmin(authContext.tokenData) && (
        <StyledTab label="Inventory Manager" value="/inventory" to="/inventory" icon={<ListAlt />} />
      )}
      {authContext.tokenData && !isAdmin(authContext.tokenData) && authContext.permittedInventores.length > 0 && (
        <StyledTab
          label={`${authContext.permittedInventores[0].name} inventory`}
          value="/inventory/:id"
          to={`/inventory/${authContext.permittedInventores[0].id}`}
          icon={<ListAlt />}
        />
      )}
      {!authContext.jwt && <StyledTab label="Login" value="/login" to="/login" icon={<LoginIcon />} />}
      {authContext.jwt && <StyledTab label="Logout" value="/logout" to="/logout" icon={<Logout />} />}
    </Tabs>
  )
}

const ProtectedRoute = ({ children, adminOnly }: { children: ReactNode; adminOnly?: boolean }) => {
  const { tokenData, authLoading, jwt } = useContext(AuthContext)
  const location = useLocation()

  if (authLoading && tokenData && jwt) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
        <CircularProgress />
      </div>
    )
  }

  if (!tokenData) {
    return <Navigate to="/login" replace state={{ from: location }} />
  } else if (adminOnly && !tokenData.permissions.some((p) => p.kind === 'admin')) {
    return <Navigate to="/" replace state={{ from: location }} />
  }

  return <>{children}</>
}

function App() {
  const [token, setToken] = useState<string | undefined>()
  const [authData, setAuthData] = useState<AuthenticationBearerJWT | undefined>()
  const [permittedInventores, setPermittedInventories] = useState<Inventory[]>([])
  const [authLoading, setAuthLoading] = useState<boolean>(true)
  const prevToken = usePrevious(token)

  useEffect(() => {
    if (prevToken !== token) {
      if (token === undefined) {
        localStorage.removeItem('chezy-inventory-token')
        axios.defaults.headers.common['authorization'] = ``
      } else {
        localStorage.setItem('chezy-inventory-token', token)
        // attaches the bearer token to every request
        axios.defaults.headers.common['authorization'] = `Bearer ${token}`
      }
    }
  }, [token, prevToken])

  const reloadUserData = useCallback(() => {
    const token = localStorage.getItem('chezy-inventory-token')
    if (token) {
      axios
        .get(`${getBaseURL()}/login`, { headers: { authorization: `Bearer ${token}` } })
        .then(
          ({ data }: { data: { token: string; authorization: AuthenticationBearerJWT; inventories: Inventory[] } }) => {
            setToken(token)
            setAuthData(data.authorization)
            setPermittedInventories(data.inventories)
            setAuthLoading(false)
          }
        )
        .catch(() => {
          localStorage.removeItem('chezy-inventory-token')
          setAuthLoading(false)
        })
    } else {
      setAuthLoading(false)
    }
  }, [])

  useEffect(() => {
    reloadUserData()
  }, [reloadUserData])

  return (
    <AuthContext.Provider
      value={{
        tokenData: authData,
        jwt: token,
        setJwt: setToken,
        setTokenData: setAuthData,
        permittedInventores,
        setPermittedInventories,
        authLoading,
        setAuthLoading,
        reloadUserData,
      }}
    >
      <Box sx={{ display: 'flex' }}>
        <AppBar component="nav">
          <Toolbar>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1, display: { xs: 'none', sm: 'block' } }}>
              Chezy Inventory
            </Typography>
            <NavTabs />
          </Toolbar>
        </AppBar>
        <Box padding={'20px'} component="main" width="100%">
          <Toolbar />
          <Routes>
            <Route path="/logout" element={<LogoutComponent />} />
            <Route path="/login" element={<Login />} />
            <Route
              path="/bin"
              element={
                <ProtectedRoute>
                  <BinBrowser />
                </ProtectedRoute>
              }
            />
            <Route
              path="/bin/:binId"
              element={
                <ProtectedRoute>
                  <BinBrowser />
                </ProtectedRoute>
              }
            />
            <Route
              path="/search"
              element={
                <ProtectedRoute>
                  <ItemSearch />
                </ProtectedRoute>
              }
            />
            <Route path="/inventory">
              <Route
                index
                element={
                  <ProtectedRoute adminOnly>
                    <InventoryManager />
                  </ProtectedRoute>
                }
              />
              <Route
                path=":id"
                element={
                  <ProtectedRoute>
                    <InventoryBrowser />
                  </ProtectedRoute>
                }
              >
                <Route
                  index
                  element={
                    <ProtectedRoute>
                      <InventoryOverview />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path=":binId"
                  element={
                    <ProtectedRoute>
                      <InventoryBinView />
                    </ProtectedRoute>
                  }
                />
              </Route>
            </Route>
            <Route path="*" element={<Navigate to="/bin" replace />} />
          </Routes>
        </Box>
      </Box>
    </AuthContext.Provider>
  )
}

export default App
